import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useContactsApi } from '../api';
import { resetError, setError, setLoading } from '../slices';
import { ContactInfoRequestFormData } from '../types/ContactInfoRequestFormData';

export const useContactService = () => {
  const dispatch = useDispatch();

  const api = useContactsApi();

  const getContactGroups = useCallback(async (companyId: string, contactId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getContactGroups: true }));

      const response = await api.getContactGroups(companyId, contactId);
      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getContactGroups: false }));
    }
  }, []);

  const deleteContact = useCallback(async (companyId: string, contactId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ delete: true }));

      await api.deleteContact(companyId, contactId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ delete: false }));
    }
  }, []);

  const updateContactInfo = useCallback(
    async (companyId: string, contactId: string, contactInfo: ContactInfoRequestFormData) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ updateContactInfo: true }));
        await api.updateContactInfo(companyId, contactId, contactInfo);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ updateContactInfo: false }));
      }
    },
    [],
  );

  const assignUser = useCallback(async (companyId: string, contactId: string, userId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ updateContactInfo: true }));
      await api.assignUser(companyId, contactId, userId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ updateContactInfo: false }));
    }
  }, []);

  const changeContactGroups = useCallback(
    async (
      companyId: string,
      contactId: string,
      newGroupIds: string[],
      removedGroupIds: string[],
    ) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ changeContactGroups: true }));
        const promises = [];
        if (newGroupIds.length) {
          promises.push(api.addContactToGroups(companyId, contactId, newGroupIds));
        }
        if (removedGroupIds.length) {
          promises.push(api.removeContactFromGroups(companyId, contactId, removedGroupIds));
        }
        await Promise.all(promises);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ changeContactGroups: false }));
      }
    },
    [],
  );

  const changeContactMetadata = useCallback(
    async (
      contactId: string,
      customFields: { metadataId: string; value: string }[],
      removedCustomFieldIds: string[],
    ) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ changeContactMetadata: true }));
        const promises = [];
        if (customFields.length) {
          promises.push(api.addContactCustomFields(contactId, customFields));
        }
        if (removedCustomFieldIds.length) {
          promises.push(api.removeContactCustomFields(contactId, removedCustomFieldIds));
        }
        await Promise.all(promises);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ changeContactMetadata: false }));
      }
    },
    [],
  );

  const optOutContact = useCallback(async (contactId: string, companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ optOutContact: true }));
      await api.optOutContact(contactId, companyId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ optOutContact: false }));
    }
  }, []);

  return {
    deleteContact,
    updateContactInfo,
    assignUser,
    getContactGroups,
    changeContactGroups,
    changeContactMetadata,
    optOutContact,
  };
};
